import { useEffect, useMemo, useState } from "react";
import {
  CalendarClock,
  HardHat,
  Hammer,
  Loader2,
  Truck,
  Wrench,
  ChevronDown,
} from "lucide-react";

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "./ui/collapsible";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "./ui/tabs";
import { motorBudgetDetail } from "../lib/api";

function fmtMoney(n) {
  if (n == null || !Number.isFinite(Number(n))) return "0 €";
  return `${new Intl.NumberFormat("es-ES", {
    maximumFractionDigits: 0,
  }).format(Math.round(Number(n)))} €`;
}

function fmtNumero(n, decimales = 1) {
  if (n == null || !Number.isFinite(Number(n))) return "0";
  return new Intl.NumberFormat("es-ES", {
    maximumFractionDigits: decimales,
  }).format(Number(n));
}

function diasALaboraleA(diasLaborables) {
  const semanas = Math.floor(diasLaborables / 5);
  const resto = diasLaborables % 5;
  if (semanas === 0) return `${diasLaborables} día${diasLaborables === 1 ? "" : "s"}`;
  if (resto === 0) return `${semanas} sem.`;
  return `${semanas} sem. ${resto} día${resto === 1 ? "" : "s"}`;
}

const TIPO_INSUMO_META = {
  material: { label: "Materiales", icono: Truck, tono: "blue" },
  mano_obra: { label: "Mano de obra", icono: HardHat, tono: "amber" },
  maquinaria: { label: "Maquinaria", icono: Wrench, tono: "purple" },
};

const COLORES_CAPITULO = [
  "#1d4ed8",
  "#0d9488",
  "#9333ea",
  "#dc2626",
  "#ea580c",
  "#16a34a",
  "#0284c7",
  "#7c3aed",
  "#65a30d",
  "#be185d",
];

function colorPorIndice(idx) {
  return COLORES_CAPITULO[idx % COLORES_CAPITULO.length];
}

export default function ProyectoDetalladoPanel({
  partidas,
  proyectoIntegral,
  contextoPrompt,
  visible,
}) {
  const [data, setData] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);

  const partidasKey = useMemo(
    () =>
      (partidas ?? [])
        .map((p) => `${p.jobcard_id ?? "?"}:${p.cantidad}:${p.precio_unitario}`)
        .join("|"),
    [partidas],
  );

  useEffect(() => {
    if (!visible) return;
    const codigos = (partidas ?? [])
      .filter((p) => p.jobcard_id)
      .map((p) => ({
        codigo: p.jobcard_id,
        cantidad: Number(p.cantidad) || 0,
        precio_unitario: Number(p.precio_unitario) || 0,
        area: p.area ?? null,
        concepto: p.concepto ?? null,
      }))
      .filter((p) => p.cantidad > 0);
    if (codigos.length === 0) {
      setData(null);
      return;
    }
    let cancelado = false;
    setCargando(true);
    setError(null);
    motorBudgetDetail({ partidas: codigos, proyectoIntegral })
      .then((resp) => {
        if (cancelado) return;
        setData(resp);
        setCargando(false);
      })
      .catch((err) => {
        if (cancelado) return;
        setCargando(false);
        setError(err?.response?.data?.message || err.message);
      });
    return () => {
      cancelado = true;
    };
  }, [partidasKey, visible, proyectoIntegral]);

  if (!visible) return null;

  return (
    <div className="rx-card p-6 space-y-5" data-testid="proyecto-detallado-panel">
      <header className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-md bg-blue-50 text-blue-600">
              <CalendarClock className="w-4 h-4" />
            </span>
            <h3 className="font-display font-bold text-lg">Proyecto detallado</h3>
          </div>
          <p className="text-sm text-slate-500 mt-1">
            Diagrama de obra, hitos y desglose de materiales, mano de obra y
            maquinaria por capítulo CYPE.
          </p>
        </div>
      </header>

      {cargando && (
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          Calculando insumos y planificación…
        </div>
      )}
      {error && !cargando && (
        <div className="text-sm text-red-600">{error}</div>
      )}
      {!cargando && !error && data && (
        <ProyectoDetalladoContenido
          data={data}
          contextoPrompt={contextoPrompt}
        />
      )}
      {!cargando && !error && !data && (
        <div className="text-sm text-slate-500">
          Añade partidas con código CYPE para ver el desglose detallado.
        </div>
      )}
    </div>
  );
}

function ProyectoDetalladoContenido({ data }) {
  const { totales, capitulos, timeline, partidas } = data;
  return (
    <div className="space-y-5">
      <ResumenCabecera totales={totales} timeline={timeline} />
      <Tabs defaultValue="diagrama" className="w-full">
        <TabsList>
          <TabsTrigger value="diagrama">Diagrama de obra</TabsTrigger>
          <TabsTrigger value="capitulos">Capítulos</TabsTrigger>
          <TabsTrigger value="insumos">Insumos por partida</TabsTrigger>
        </TabsList>
        <TabsContent value="diagrama" className="pt-4">
          <DiagramaGantt
            timeline={timeline}
            capitulos={capitulos}
          />
        </TabsContent>
        <TabsContent value="capitulos" className="pt-4">
          <CapitulosBreakdown capitulos={capitulos} />
        </TabsContent>
        <TabsContent value="insumos" className="pt-4">
          <InsumosPorPartida partidas={partidas} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ResumenCabecera({ totales, timeline }) {
  const items = [
    {
      label: "Coste total estimado",
      valor: fmtMoney(totales.coste),
      sub: "Materiales + mano de obra + maquinaria",
      icon: Hammer,
    },
    {
      label: "Duración estimada",
      valor: diasALaboraleA(totales.dias),
      sub: `${fmtNumero(totales.horas, 0)} h con ${timeline.num_operarios} operarios`,
      icon: CalendarClock,
    },
    {
      label: "Operarios sugeridos",
      valor: `${timeline.num_operarios}`,
      sub: `Jornada de ${timeline.horas_jornada} h`,
      icon: HardHat,
    },
  ];
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      {items.map((item) => {
        const Icon = item.icon;
        return (
          <div
            key={item.label}
            className="p-3 rounded-md border border-slate-200 bg-slate-50"
          >
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-slate-500 font-bold">
              <Icon className="w-3.5 h-3.5" />
              {item.label}
            </div>
            <div className="font-display font-bold text-xl mt-1">
              {item.valor}
            </div>
            <div className="text-xs text-slate-500 mt-1">{item.sub}</div>
          </div>
        );
      })}
    </div>
  );
}

function DiagramaGantt({ timeline, capitulos }) {
  const diasTotal = Math.max(timeline.dias_total ?? 0, 1);
  const ticks = useMemo(() => {
    const step = diasTotal <= 14 ? 2 : diasTotal <= 60 ? 5 : 10;
    const out = [];
    for (let d = 0; d <= diasTotal; d += step) out.push(d);
    if (out[out.length - 1] !== diasTotal) out.push(diasTotal);
    return out;
  }, [diasTotal]);

  if (timeline.items.length === 0) {
    return (
      <div className="text-sm text-slate-500">
        No hay capítulos planificables.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="rounded-md border border-slate-200 overflow-x-auto">
        <div className="min-w-[640px]">
          <div className="grid grid-cols-[180px_1fr] border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wider text-slate-500 font-bold">
            <div className="px-3 py-2">Capítulo</div>
            <div className="relative px-3 py-2">
              <div className="flex justify-between">
                {ticks.map((t) => (
                  <span key={t}>D{t}</span>
                ))}
              </div>
            </div>
          </div>
          <div className="divide-y divide-slate-100">
            {timeline.items.map((item, idx) => {
              const cap = capitulos.find(
                (c) => c.capitulo.id === item.capitulo_id,
              );
              const left = (item.dia_inicio / diasTotal) * 100;
              const width = ((item.dia_fin - item.dia_inicio) / diasTotal) * 100;
              const color = colorPorIndice(idx);
              return (
                <div
                  key={item.id}
                  className="grid grid-cols-[180px_1fr] items-center"
                >
                  <div className="px-3 py-2">
                    <div className="text-sm font-medium text-slate-800">
                      {idx + 1}. {item.label}
                    </div>
                    <div className="text-xs text-slate-500">
                      {diasALaboraleA(item.dia_fin - item.dia_inicio)} ·{" "}
                      {fmtMoney(cap?.coste_total)}
                    </div>
                  </div>
                  <div className="relative h-10 bg-slate-50">
                    <div
                      className="absolute top-1/2 -translate-y-1/2 h-5 rounded-md shadow-sm flex items-center px-2"
                      style={{
                        left: `${left}%`,
                        width: `${Math.max(width, 1.5)}%`,
                        backgroundColor: color,
                      }}
                      title={`${item.label} · D${item.dia_inicio} → D${item.dia_fin}`}
                    >
                      <span className="text-[10px] font-bold text-white truncate">
                        D{item.dia_inicio}–D{item.dia_fin}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <HitosLista hitos={timeline.hitos} diasTotal={diasTotal} />
    </div>
  );
}

function HitosLista({ hitos, diasTotal }) {
  if (!hitos || hitos.length === 0) return null;
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest font-bold text-slate-500 mb-2">
        Hitos del proyecto
      </div>
      <div className="space-y-1">
        {hitos.map((h, idx) => {
          const pct = (h.dia / Math.max(diasTotal, 1)) * 100;
          return (
            <div
              key={h.capitulo_id}
              className="grid grid-cols-[60px_1fr] items-center text-xs"
            >
              <div className="text-slate-500">D{h.dia}</div>
              <div className="relative h-2 bg-slate-100 rounded">
                <span
                  className="absolute top-0 bottom-0 w-1 bg-emerald-600 rounded"
                  style={{ left: `${pct}%` }}
                />
                <div
                  className="absolute -top-0.5 text-[11px] text-slate-700 whitespace-nowrap"
                  style={{ left: `calc(${pct}% + 6px)` }}
                >
                  H{idx + 1} · {h.label}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CapitulosBreakdown({ capitulos }) {
  if (capitulos.length === 0) {
    return (
      <div className="text-sm text-slate-500">
        Aún no hay datos de desglose.
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {capitulos.map((g, idx) => {
        const total = g.coste_total || 1;
        const segs = [
          { label: "Materiales", value: g.importe_materiales, color: "#1d4ed8" },
          { label: "Mano de obra", value: g.importe_mano_obra, color: "#d97706" },
          { label: "Maquinaria", value: g.importe_maquinaria, color: "#7c3aed" },
        ];
        return (
          <div
            key={g.capitulo.id}
            className="rounded-md border border-slate-200 p-3"
          >
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-medium text-slate-800">
                  Capítulo {idx + 1} · {g.capitulo.nombre}
                </div>
                <div className="text-xs text-slate-500">
                  {g.codigos.length} partida
                  {g.codigos.length === 1 ? "" : "s"} ·{" "}
                  {fmtNumero(g.horas_estimadas, 0)} h ·{" "}
                  {diasALaboraleA(g.dias_estimados)}
                </div>
              </div>
              <div className="text-right">
                <div className="font-display font-bold text-lg">
                  {fmtMoney(g.coste_total)}
                </div>
              </div>
            </div>
            <div className="mt-3 h-2 rounded-full bg-slate-100 overflow-hidden flex">
              {segs.map((s) => {
                const pct = (s.value / total) * 100;
                return (
                  <div
                    key={s.label}
                    style={{ width: `${pct}%`, backgroundColor: s.color }}
                    title={`${s.label}: ${fmtMoney(s.value)}`}
                  />
                );
              })}
            </div>
            <div className="mt-2 grid grid-cols-3 gap-2 text-[11px] text-slate-600">
              {segs.map((s) => (
                <div key={s.label} className="flex items-center gap-1">
                  <span
                    className="inline-block w-2 h-2 rounded-sm"
                    style={{ backgroundColor: s.color }}
                  />
                  <span className="font-medium text-slate-700">{s.label}:</span>
                  <span>{fmtMoney(s.value)}</span>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function InsumosPorPartida({ partidas }) {
  if (partidas.length === 0) return null;
  return (
    <div className="space-y-2">
      {partidas.map((p) => (
        <PartidaInsumosRow key={p.codigo} partida={p} />
      ))}
    </div>
  );
}

function PartidaInsumosRow({ partida }) {
  const [open, setOpen] = useState(false);
  const tieneInsumos =
    partida.insumos.material.length > 0 ||
    partida.insumos.mano_obra.length > 0 ||
    partida.insumos.maquinaria.length > 0;

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className="rounded-md border border-slate-200">
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="w-full flex items-center justify-between px-3 py-2 hover:bg-slate-50"
          >
            <div className="text-left">
              <div className="text-sm font-medium text-slate-800">
                {partida.codigo} · {partida.titulo ?? "Sin título"}
              </div>
              <div className="text-xs text-slate-500">
                {fmtNumero(partida.cantidad, 2)} {partida.unidad ?? ""} ·{" "}
                {fmtMoney(partida.importe_total)} ·{" "}
                {fmtNumero(partida.horas_estimadas, 1)} h
              </div>
            </div>
            <ChevronDown
              className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`}
            />
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="border-t border-slate-200 p-3 space-y-3">
            {!partida.tiene_datos_cype && (
              <div className="text-xs text-amber-700 bg-amber-50 rounded px-2 py-1">
                No hay desglose CYPE para esta partida.
              </div>
            )}
            {tieneInsumos && (
              <>
                <InsumosTabla
                  tipo="material"
                  items={partida.insumos.material}
                  cantidad={partida.cantidad}
                />
                <InsumosTabla
                  tipo="mano_obra"
                  items={partida.insumos.mano_obra}
                  cantidad={partida.cantidad}
                />
                <InsumosTabla
                  tipo="maquinaria"
                  items={partida.insumos.maquinaria}
                  cantidad={partida.cantidad}
                />
              </>
            )}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}

function InsumosTabla({ tipo, items, cantidad }) {
  if (!items || items.length === 0) return null;
  const meta = TIPO_INSUMO_META[tipo];
  const Icon = meta.icono;
  const totalImporte = items.reduce(
    (s, ins) => s + ins.importe * cantidad,
    0,
  );
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500 font-bold">
          <Icon className="w-3.5 h-3.5" />
          {meta.label}
        </div>
        <div className="text-xs font-medium text-slate-700">
          {fmtMoney(totalImporte)}
        </div>
      </div>
      <div className="overflow-x-auto rounded border border-slate-200">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-slate-500">
            <tr>
              <th className="text-left px-2 py-1 font-medium">Descripción</th>
              <th className="text-right px-2 py-1 font-medium">Rend./ud</th>
              <th className="text-right px-2 py-1 font-medium">Total</th>
              <th className="text-right px-2 py-1 font-medium">Ud</th>
              <th className="text-right px-2 py-1 font-medium">Precio</th>
              <th className="text-right px-2 py-1 font-medium">Importe</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.map((ins, idx) => {
              const totalCantidad = ins.rendimiento * cantidad;
              const totalImporteIns = ins.importe * cantidad;
              return (
                <tr key={`${ins.codigo ?? idx}-${idx}`}>
                  <td className="px-2 py-1 text-slate-700">
                    {ins.descripcion ?? ins.codigo ?? "—"}
                  </td>
                  <td className="px-2 py-1 text-right">
                    {fmtNumero(ins.rendimiento, 3)}
                  </td>
                  <td className="px-2 py-1 text-right">
                    {fmtNumero(totalCantidad, 2)}
                  </td>
                  <td className="px-2 py-1 text-right text-slate-500">
                    {ins.unidad ?? ""}
                  </td>
                  <td className="px-2 py-1 text-right">
                    {fmtMoney(ins.precio_unitario)}
                  </td>
                  <td className="px-2 py-1 text-right font-medium">
                    {fmtMoney(totalImporteIns)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
